import AddressCard from "@/components/address-card"
import LogisticCard from "@/components/logistic-card"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { addDeliveryToCart, getCart } from "@/queries/cart"
import { getLogisticsClient } from "@/queries/clients"
import type { CartItem } from "@/types/shop"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowRight } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import dayjs from "dayjs"

export const Route = createFileRoute("/shop/logistic/")({
  component: RouteComponent,
})

function RouteComponent() {
  const logisticQuery = useQuery({
    queryFn: () => getLogisticsClient(),
    queryKey: ["logistic-options"],
  })

  const getCartQuery = useQuery({
    queryFn: () => getCart(),
    queryKey: ["shoping-cart"],
  })

  const [cart, setCart] = useState<Array<CartItem>>([])
  const navigate = useNavigate()

  useEffect(() => {
    if (getCartQuery.data) {
      setCart(Object.values(getCartQuery.data.data.cart.items))
    }
  }, [getCartQuery.data])

  const [selectedLogistic, setSelectedLogistic] = useState<any>()
  const [selectedAddress, setSelectedAddress] = useState<any>(null)

  const addDeliveryMutation = useMutation({
    mutationFn: () => {
      let data: any = {
        date: dayjs(selectedLogistic.selectedDate).format("YYYY-MM-DD"),
        address_id: selectedAddress.id,
      }
      if (selectedLogistic.selectedTime) {
        data = {
          ...data,
          deliveryBetweenStart: selectedLogistic.selectedTime
            .split("-")[0]
            .replace(":00", "")
            .trim(),
          deliveryBetweenEnd: selectedLogistic.selectedTime
            .split("-")[1]
            .replace(":00", "")
            .trim(),
        }
      }
      return addDeliveryToCart(selectedLogistic.logistic.id, data)
    },
    mutationKey: ["addDelivery"],
    onSuccess() {
      toast.success("با موفقیت اضافه شد")
      navigate({ to: "/shop/checkout" })
    },
    onError() {
      toast.error("خطایی در اضافه کردن آدرس پیش آمده است")
    },
  })

  return (
    <>
      <div className="flex items-center py-10">
        <Link to="/shop/cart">
          <Button variant="ghost" size="icon">
            <ArrowRight />
          </Button>
        </Link>
        <h1 className="text-xl font-bold">انتخاب زمان و نحوه ارسال</h1>
      </div>

      <Separator />

      <div className="grid grid-cols-1 lg:grid-cols-8 gap-5 mt-2">
        <div className="col-span-1 md:col-span-5">
          <AddressCard
            selectedAddress={selectedAddress}
            setSelectedAddress={setSelectedAddress}
          />
          <Separator />
          {selectedAddress ? (
            <div className="mt-10 space-y-4 grid grid-cols-1 md:grid-cols-2">
              {logisticQuery.isLoading && <div>در حال بارگذاری ...</div>}
              {logisticQuery.data?.data?.data?.map((logistic: any) => (
                <LogisticCard
                  logistic={logistic}
                  key={logistic.id}
                  isSelected={selectedLogistic?.id == logistic?.id}
                  onSubmit={setSelectedLogistic}
                />
              ))}
              {logisticQuery.data?.data?.data?.length === 0 &&
                !logisticQuery.isLoading && (
                  <div>هیچ گزینه‌ای برای ارسال موجود نیست.</div>
                )}
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center h-52">
              <p className="text-lg font-bold">
                برای انتخاب زمان و نحوه ارسال ابتدا یک آدرس مشخص کنید
              </p>
            </div>
          )}
        </div>
        <div className="col-span-1 md:col-span-3">
          <Card className="sticky top-50">
            <CardHeader className="flex-row justify-between">
              <CardTitle>سفارش شما</CardTitle>
              {cart.length} کالا
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              <p className="">جمع کالا ها: </p>
              <p className="text-end">
                {Intl.NumberFormat().format(
                  cart.reduce((acc, curr) => {
                    return acc + curr.product.price * curr.quantity
                  }, 0)
                )}{" "}
                ریال
              </p>

              {selectedLogistic && (
                <>
                  <p>هزینه ارسال: </p>
                  <p className="text-end">
                    {Intl.NumberFormat().format(
                      selectedLogistic?.logistic?.price
                    )}{" "}
                    ریال
                  </p>
                </>
              )}

              {cart.filter((c) => c.discount_price).length > 0 && (
                <>
                  <p>مجموع تخفیف:</p>
                  <p className="text-end">
                    {Intl.NumberFormat().format(
                      cart.reduce((acc, curr) => {
                        if (!curr.discount_price) {
                          return acc
                        }
                        return acc + curr.discount_price
                      }, 0)
                    )}{" "}
                    ریال
                  </p>
                </>
              )}

              <Separator className="col-span-2" />
              <p>مبلغ پرداختی: </p>
              <p className="text-end">
                {Intl.NumberFormat().format(
                  cart.reduce((acc, curr) => {
                    return (
                      acc +
                      curr.product.price * curr.quantity -
                      (curr.discount_price || 0)
                    )
                  }, 0) + (selectedLogistic?.logistic.price || 0)
                )}{" "}
                ریال
              </p>

              <Separator className="col-span-2" />
              <div className="col-span-2">
                <Button
                  className="w-full"
                  disabled={!selectedAddress || !selectedLogistic}
                  onClick={() => addDeliveryMutation.mutate()}
                  isLoading={addDeliveryMutation?.isPending}
                >
                  ادامه
                </Button>
                {!selectedAddress ||
                  (!selectedLogistic && (
                    <Alert variant="destructive" className="text-sm mt-2">
                      برای ادامه مراحل یک آدرس و زمان ارسال مشخص کنید
                    </Alert>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}
